import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Copy } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

export const MfaEnrollStep = ({ mfaToken, onDone, onCancel }) => {
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.post("/auth/mfa/login-setup", { mfa_token: mfaToken })
            .then((r) => setSetup(r.data))
            .catch((e) => setErr(formatDetail(e?.response?.data?.detail) || e.message));
    }, [mfaToken]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            const r = await api.post("/auth/mfa/login-setup-verify", { mfa_token: mfaToken, code: code.trim() });
            onDone(r.data);
        } catch (e2) {
            setErr(formatDetail(e2?.response?.data?.detail) || e2.message);
            setCode("");
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="mfa-enroll-step">
            <div className="flex items-center gap-2 text-slate-800 font-medium">
                <ShieldCheck className="w-4 h-4" /> Set up two-factor authentication
            </div>
            <p className="text-xs text-slate-500 -mt-2">Two-factor authentication is mandatory. Scan the QR code with Google Authenticator, Authy or 1Password, then enter the 6-digit code.</p>
            {!setup && !err && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
            {setup && (
                <>
                    <img src={setup.qr_data_url} alt="MFA QR code" data-testid="mfa-enroll-qr" className="w-44 h-44 mx-auto border border-slate-200 rounded-md" />
                    <div className="text-[11px] text-slate-500 text-center">
                        Or enter key manually: <code data-testid="mfa-enroll-secret" className="font-mono text-slate-800 break-all">{setup.secret}</code>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="mfa-enroll-backup-codes">
                        <div className="flex items-center justify-between text-xs font-semibold text-amber-900 mb-1.5">
                            Backup codes — save them now, shown once
                            <button type="button" data-testid="mfa-enroll-copy-codes" onClick={() => navigator.clipboard?.writeText(setup.backup_codes.join("\n"))}
                                    className="flex items-center gap-1 text-amber-800 hover:text-amber-950"><Copy className="w-3 h-3" /> Copy</button>
                        </div>
                        <div className="grid grid-cols-2 gap-1 font-mono text-[11px] text-amber-950">
                            {setup.backup_codes.map((c) => <span key={c}>{c}</span>)}
                        </div>
                    </div>
                    <input data-testid="mfa-enroll-code" autoFocus required inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                           value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                           className="input tracking-widest text-lg text-center" placeholder="123456" />
                </>
            )}
            {err && <div data-testid="mfa-enroll-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2.5">{err}</div>}
            <button data-testid="mfa-enroll-submit" disabled={busy || !setup || code.length !== 6} type="submit"
                    className="btn-primary w-full py-2.5 rounded-md font-medium flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verify &amp; sign in
            </button>
            <button type="button" data-testid="mfa-enroll-cancel" onClick={onCancel} className="w-full text-xs text-slate-500 hover:text-slate-800">
                ← Cancel
            </button>
        </form>
    );
};
